import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

export default function CargosManager({ usuario }) {
    const [cargos, setCargos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [showDocConfig, setShowDocConfig] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [configuringCargo, setConfiguringCargo] = useState(null);
    const [availableDocs, setAvailableDocs] = useState([]);
    const [selectedDocs, setSelectedDocs] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [showArticulosConfig, setShowArticulosConfig] = useState(false);
    const [configuringCargoArticulos, setConfiguringCargoArticulos] = useState(null);
    const [availableArticulos, setAvailableArticulos] = useState([]);
    const [selectedArticulos, setSelectedArticulos] = useState([]);
    const [loadingArticulos, setLoadingArticulos] = useState(false);

    const [formData, setFormData] = useState({
        cargo: ''
    });

    useEffect(() => {
        if (usuario?.empresa_id) {
            fetchCargos();
        }
    }, [usuario]);

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchCargos = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/cargos?id_empresa=${usuario.empresa_id}`);
            if (response.ok) {
                setCargos(await response.json());
            } else {
                setError('Error al cargar cargos');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const url = editingId
                ? `${API_BASE_URL}/api/cargos/${editingId}`
                : `${API_BASE_URL}/api/cargos`;

            const method = editingId ? 'PUT' : 'POST';

            const response = await fetchWithAuth(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, id_empresa: usuario.empresa_id })
            });

            if (response.ok) {
                fetchCargos();
                setShowForm(false);
                setEditingId(null);
                setFormData({ cargo: '' });
            } else {
                const data = await response.json();
                setError(data.error || 'Error al guardar');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (c) => {
        setEditingId(c.id_cargo);
        setFormData({
            cargo: c.cargo
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar este cargo?')) return;
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/cargos/${id}`, { method: 'DELETE' });
            if (response.ok) fetchCargos();
            else alert('Error al eliminar');
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const handleConfigureDocs = async (cargo) => {
        setConfiguringCargo(cargo);
        setShowDocConfig(true);
        setLoadingDocs(true);

        try {
            // Fetch available docs (filtered by base=false and entidad='P')
            const docsResponse = await fetchWithAuth(`${API_BASE_URL}/api/tipo-documentos?empresa_id=${usuario.empresa_id}`);
            const allDocs = await docsResponse.json();
            // Filter: Not base, and applies to Persons (P) or NULL (assumed general)
            const filteredDocs = allDocs.filter(d => !d.base && (d.tipo_entidad === 'P' || !d.tipo_entidad));
            setAvailableDocs(filteredDocs);

            // Fetch currently assigned docs
            const assignedResponse = await fetchWithAuth(`${API_BASE_URL}/api/cargos/${cargo.id_cargo}/documentos`);
            if (assignedResponse.ok) {
                const assignedIds = await assignedResponse.json();
                setSelectedDocs(assignedIds);
            } else {
                setSelectedDocs([]);
            }
        } catch (err) {
            console.error(err);
            alert('Error al cargar configuración de documentos');
        } finally {
            setLoadingDocs(false);
        }
    };

    const saveDocConfiguration = async () => {
        setLoadingDocs(true);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/cargos/${configuringCargo.id_cargo}/documentos`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ documentos_ids: selectedDocs })
            });

            if (response.ok) {
                setShowDocConfig(false);
                setConfiguringCargo(null);
                alert('Configuración guardada exitosamente');
            } else {
                alert('Error al guardar configuración');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setLoadingDocs(false);
        }
    };

    const toggleDocSelection = (docId) => {
        setSelectedDocs(prev =>
            prev.includes(docId)
                ? prev.filter(id => id !== docId)
                : [...prev, docId]
        );
    };

    const handleConfigureArticulos = async (cargo) => {
        setConfiguringCargoArticulos(cargo);
        setShowArticulosConfig(true);
        setLoadingArticulos(true);

        try {
            const articulosResponse = await fetchWithAuth(`${API_BASE_URL}/api/articulos-seguridad?id_empresa=${usuario.empresa_id}`);
            const allArticulos = await articulosResponse.json();
            setAvailableArticulos(allArticulos);

            const assignedResponse = await fetchWithAuth(`${API_BASE_URL}/api/cargos/${cargo.id_cargo}/articulos-seguridad`);
            if (assignedResponse.ok) {
                const assignedIds = await assignedResponse.json();
                setSelectedArticulos(assignedIds);
            } else {
                setSelectedArticulos([]);
            }
        } catch (err) {
            console.error(err);
            alert('Error al cargar configuración de artículos');
        } finally {
            setLoadingArticulos(false);
        }
    };

    const saveArticulosConfiguration = async () => {
        setLoadingArticulos(true);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/cargos/${configuringCargoArticulos.id_cargo}/articulos-seguridad`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ articulos_ids: selectedArticulos })
            });

            if (response.ok) {
                setShowArticulosConfig(false);
                setConfiguringCargoArticulos(null);
                alert('Configuración guardada exitosamente');
            } else {
                alert('Error al guardar configuración');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setLoadingArticulos(false);
        }
    };

    const toggleArticuloSelection = (artId) => {
        setSelectedArticulos(prev =>
            prev.includes(artId)
                ? prev.filter(id => id !== artId)
                : [...prev, artId]
        );
    };

    return (
        <div className="manager-container">
            <div className="header-actions">
                <h2>Gestión de Cargos</h2>
                <button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ cargo: '' }); }} className="add-button">
                    + Nuevo Cargo
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {showForm && (
                <div className="modal">
                    <form onSubmit={handleSubmit} className="standard-form" style={{ width: '100%', maxWidth: '500px' }}>
                        <h3 style={{ marginBottom: '24px', color: 'var(--sidebar-bg)', fontSize: '1.5rem' }}>
                            {editingId ? '📝 Editar' : '✨ Nuevo'} Cargo
                        </h3>

                        <div className="form-group">
                            <label>Nombre del Cargo</label>
                            <input
                                value={formData.cargo}
                                onChange={e => setFormData({ ...formData, cargo: e.target.value })}
                                placeholder="Ej: Supervisor"
                                required
                            />
                        </div>

                        <div className="form-actions" style={{ marginTop: '32px' }}>
                            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary" style={{ marginRight: 'auto' }}>
                                Cancelar
                            </button>
                            <button type="submit" disabled={loading} className="add-button">
                                {editingId ? 'Actualizar' : 'Guardar'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {showDocConfig && (
                <div className="modal">
                    <div className="standard-form" style={{ width: '100%', maxWidth: '600px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                        <h3 style={{ marginBottom: '16px', color: 'var(--sidebar-bg)', fontSize: '1.4rem' }}>
                            📄 Documentos para: {configuringCargo?.cargo}
                        </h3>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>
                            Selecciona los documentos adicionales requeridos para este cargo.
                        </p>

                        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px' }}>
                            {loadingDocs ? <p>Cargando...</p> : availableDocs.map(doc => (
                                <label key={doc.id} style={{ display: 'flex', alignItems: 'center', padding: '8px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedDocs.includes(doc.id)}
                                        onChange={() => toggleDocSelection(doc.id)}
                                        style={{ width: '18px', height: '18px', marginRight: '10px' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: '500' }}>{doc.tipo_documento}</div>
                                        {doc.descripcion && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{doc.descripcion}</div>}
                                    </div>
                                </label>
                            ))}
                            {!loadingDocs && availableDocs.length === 0 && (
                                <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>
                                    No hay documentos disponibles para configurar.
                                </p>
                            )}
                        </div>

                        <div className="form-actions" style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                            <button type="button" onClick={() => setShowDocConfig(false)} className="btn-secondary" style={{ marginRight: 'auto' }}>
                                Cerrar
                            </button>
                            <button type="button" onClick={saveDocConfiguration} className="add-button" disabled={loadingDocs}>
                                {loadingDocs ? 'Guardando...' : 'Guardar Configuración'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showArticulosConfig && (
                <div className="modal">
                    <div className="standard-form" style={{ width: '100%', maxWidth: '600px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                        <h3 style={{ marginBottom: '16px', color: 'var(--sidebar-bg)', fontSize: '1.4rem' }}>
                            🛡️ Artículos de Seguridad para: {configuringCargoArticulos?.cargo}
                        </h3>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>
                            Selecciona los artículos de seguridad/EPP que deben ser entregados a este cargo.
                        </p>

                        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px' }}>
                            {loadingArticulos ? <p>Cargando...</p> : availableArticulos.map(art => (
                                <label key={art.id} style={{ display: 'flex', alignItems: 'center', padding: '8px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedArticulos.includes(art.id)}
                                        onChange={() => toggleArticuloSelection(art.id)}
                                        style={{ width: '18px', height: '18px', marginRight: '10px' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: '500' }}>{art.nombre}</div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                            {art.categoria ? `${art.categoria}` : ''}
                                            {art.requiere_talla ? ' · Requiere talla' : ''}
                                        </div>
                                    </div>
                                </label>
                            ))}
                            {!loadingArticulos && availableArticulos.length === 0 && (
                                <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>
                                    No hay artículos de seguridad disponibles. Crea artículos primero en el mantenedor de artículos.
                                </p>
                            )}
                        </div>

                        <div className="form-actions" style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                            <button type="button" onClick={() => setShowArticulosConfig(false)} className="btn-secondary" style={{ marginRight: 'auto' }}>
                                Cerrar
                            </button>
                            <button type="button" onClick={saveArticulosConfiguration} className="add-button" disabled={loadingArticulos}>
                                {loadingArticulos ? 'Guardando...' : 'Guardar Configuración'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? <p>Cargando cargos...</p> : (
                <table className="standard-table">
                    <thead>
                        <tr>
                            <th>Cargo</th>
                            <th style={{ width: '320px' }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cargos.map(c => (
                            <tr key={c.id_cargo}>
                                <td data-label="Cargo" style={{ fontWeight: '600' }}>{c.cargo}</td>
                                <td data-label="Acciones">
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => handleConfigureDocs(c)}
                                            className="btn-action"
                                            title="Configurar Documentos"
                                            style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}
                                        >
                                            📄 Docs
                                        </button>
                                        <button
                                            onClick={() => handleConfigureArticulos(c)}
                                            className="btn-action"
                                            title="Configurar Artículos de Seguridad"
                                            style={{ background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' }}
                                        >
                                            🛡️ Artículos
                                        </button>
                                        <button onClick={() => handleEdit(c)} className="btn-action btn-edit" title="Editar">📝</button>
                                        <button onClick={() => handleDelete(c.id_cargo)} className="btn-action btn-delete" title="Eliminar">🗑️</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {cargos.length === 0 && (
                            <tr><td colSpan="2" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>No hay cargos registrados.</td></tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
}
